import React from "react";
import {Platform} from "react-native";
import {gql, useApolloClient} from "@apollo/client";
import {string} from "yup";
import type {FormikHelpers} from "formik";
import {
    Stack,
    SubmitButton,
    Box,
    ErrorMessage,
    Form,
    FormGroup,
    TextInput,
    View,
    Text,
    useLocalStorage,
    Inline,
    Heading,
} from "@unibuddy/patron";
import {useAnalytics} from "@unibuddy/tracking";
import useAuth from "ubcommunity-shared/src/Auth/hooks/useAuth";
import RelativeLink from "ubcommunity-shared/src/Navigation/RelativeLink/RelativeLink";
import {Routes} from "ubcommunity-shared/src/constants/routes";
import {Link} from "ubcommunity-shared/src/General/Link/Link";
import {useHistory} from "react-router";
import {TrackEvents, SignUpSources, Auth} from "ubcommunity-shared/src/constants";
import {PasswordInput} from "ubcommunity-shared/src/Auth/PasswordInput/PasswordInput";
import {useQueryParams} from "ubcommunity-shared/src/Hooks/useQueryParams";
import {useIsFeatureEnabledBoolean} from "ubcommunity-shared/src/Hooks/useIsFeatureEnabledBoolean";
import {SSO_FEATURE_FLAG} from "ubcommunity-shared/src/featureFlags";
import {StepIndicator} from "../Signup/StepIndicator";
import {MutationErrorHandler} from "ubcommunity-shared/src/General/Errors/MutationErrorHandler";
import {SocialLogin} from "../SSO/SocialLogin";

export const loginQuery = gql`
    query Login($email: String!, $password: String!) {
        getToken(email: $email, password: $password, redirectUrl: "/", rootUrl: "ONIX_URL") {
            accessToken
            me {
                anyUser {
                    id
                    verified
                    firstName
                    lastName
                    profilePhoto
                    email
                    accountRole
                    country {
                        name
                        id
                        code
                    }
                    university {
                        id
                        slug
                        name
                    }
                }
            }
        }
    }
`;

interface LoginFormValues {
    email: string;
    password: string;
}

export default function Login() {
    const client = useApolloClient();
    const {trackEvent} = useAnalytics();
    const {setAuth} = useAuth();
    // we check local storage to see if user came from the invite page and logged in
    const {push} = useHistory();
    const {getParams} = useQueryParams();
    const [inviteId] = useLocalStorage("community-invite-id.v1");
    const source = getParams("source");
    const socialLoginEnabledFeatureFlag = useIsFeatureEnabledBoolean(SSO_FEATURE_FLAG);

    const onLogin = async (
        {email, password}: {email: string; password: string},
        formActions: FormikHelpers<LoginFormValues>,
    ) => {
        try {
            const {data} = await client.query({
                query: loginQuery,
                fetchPolicy: "network-only",
                variables: {
                    email,
                    password,
                },
            });
            trackEvent(TrackEvents.PROSPECT_LOGGED_IN);
            setAuth(data.getToken);

            // if user came from invite flow send them back to the invite page
            if (inviteId && data.getToken.me.anyUser.verified && source === SignUpSources.INVITE) {
                push(`/invite/${inviteId}`);
            }
        } catch (error) {
            formActions.setFieldError("serverError", error.message);
            console.log(error);
        } finally {
            formActions.setSubmitting(false);
        }
    };

    return (
        <>
            <StepIndicator.BarIndicator currentStep={1} totalSteps={1} />
            <Form
                id="login-form"
                onSubmit={onLogin}
                initialValues={{email: "", password: "", serverError: ""}}
                validationSchema={{
                    email: string().required().email(),
                    password: string().required(),
                }}
            >
                {({errors}) => (
                    <Box minW={["100%", "100%", 0]} padding="medium-large" paddingTop="large">
                        <Stack space="large">
                            <Stack align="center" space="medium">
                                <Heading level="1" size="xxsmall" weight="700" align="center">
                                    Welcome back!
                                </Heading>
                                <View width="100%">
                                    <Inline verticalAlign="center" space="xxsmall">
                                        <Text nowrap size="small">
                                            Don't have an account?
                                        </Text>
                                        <RelativeLink to={Routes.SIGNUP_STEP_1}>
                                            <Text
                                                size="small"
                                                color="textLinkColor"
                                                style={{
                                                    textDecorationLine: "underline",
                                                    textDecorationColor: "textLinkColor",
                                                }}
                                                weight="bold"
                                            >
                                                Create an account
                                            </Text>
                                        </RelativeLink>
                                    </Inline>
                                </View>
                            </Stack>
                            {socialLoginEnabledFeatureFlag ? <SocialLogin /> : null}
                            <Stack space="medium">
                                <FormGroup label="Email">
                                    <TextInput
                                        {...Platform.select({
                                            web: {
                                                type: "email",
                                            },
                                            native: {
                                                keyboardType: "email-address",
                                                autoCompleteType: "email",
                                                importantForAutofill: "yes",
                                                textContentType: "emailAddress",
                                            },
                                        })}
                                        name="email"
                                        required
                                    />
                                </FormGroup>
                                <PasswordInput name="password" required />
                                {errors?.serverError ? (
                                    <ErrorMessage
                                        component={
                                            <MutationErrorHandler
                                                error={errors.serverError}
                                                meta={{
                                                    component: "Login",
                                                    mutation: "loginQuery",
                                                }}
                                            />
                                        }
                                        name="serverError"
                                    />
                                ) : null}
                                <View>
                                    {Platform.select({
                                        web: (
                                            <RelativeLink to="/auth/request-reset">
                                                Forgot your password?
                                            </RelativeLink>
                                        ),
                                        native: (
                                            <Link href="https://community.unibuddy.co/auth/request-reset">
                                                Forgot your password?
                                            </Link>
                                        ),
                                    })}
                                </View>

                                <SubmitButton color="primary" block>
                                    {Auth.PRIMARY_CTA}
                                </SubmitButton>
                            </Stack>
                        </Stack>
                    </Box>
                )}
            </Form>
        </>
    );
}
